import { useEffect, useMemo, useState } from 'react';
import { cn } from '@/utils/cn';

export function userInitials(firstName?: string, lastName?: string | null) {
  return `${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`.toUpperCase() || '?';
}

interface UserAvatarProps {
  firstName?: string;
  lastName?: string | null;
  imageUrl?: string | null;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}

const sizeClasses = {
  sm: 'h-8 w-8 text-xs',
  md: 'h-10 w-10 text-sm',
  lg: 'h-16 w-16 text-lg',
  xl: 'h-24 w-24 text-2xl',
} as const;

export function UserAvatar({
  firstName,
  lastName,
  imageUrl,
  size = 'md',
  className,
}: UserAvatarProps) {
  const initials = useMemo(() => userInitials(firstName, lastName), [firstName, lastName]);
  const label = `${firstName ?? ''} ${lastName ?? ''}`.trim() || 'User';
  const [broken, setBroken] = useState(false);

  useEffect(() => {
    setBroken(false);
  }, [imageUrl]);

  if (imageUrl && !broken) {
    return (
      <img
        src={imageUrl}
        alt={label}
        onError={() => setBroken(true)}
        className={cn(
          'shrink-0 rounded-full object-cover ring-2 ring-white shadow-sm dark:ring-slate-900',
          sizeClasses[size],
          className,
        )}
      />
    );
  }

  return (
    <div
      className={cn(
        'flex shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-primary-500 to-primary-700 font-semibold text-white shadow-sm ring-2 ring-white dark:ring-slate-900',
        sizeClasses[size],
        className,
      )}
      aria-hidden
    >
      {initials}
    </div>
  );
}
